import { ApolloClient, ApolloLink, DefaultContext, HttpLink, concat } from '@apollo/client';
import { useRef } from 'react';

import * as graphql from '../../../../src/generated/graphql';

import { Account, LegacyAccount, useKeychain } from './useKeychain';

export function useHttpLink<T>(client: () => ApolloClient<T>, workspaceID: string) {
	const keychain = useKeychain();
	const requestQueue = useRef<(() => void)[]>([]).current;

	async function refresh(identity: Account | LegacyAccount): Promise<void> {
		try {
			const response = await client().mutate({
				mutation: graphql.RefreshSessionDocument,
				variables: {
					input: {
						refreshToken: identity.refreshToken,
						state: 'state' in identity ? identity.state : undefined,
					},
				},
				context: { account: null },
			});

			const { accessToken, refreshToken, userID } = response.data.refreshSession.session;

			keychain.add(
				{ workspaceID, userID },
				{
					accessToken,
					refreshToken: refreshToken || identity.refreshToken,
					state: 'state' in identity ? identity.state : undefined,
				},
				'updatedAt' in identity ? identity.updatedAt : Date.now(),
			);

			for (const request of requestQueue.splice(0, requestQueue.length)) {
				request();
			}
		} catch (_) {
			keychain.delete();

			window.location.href = '/authentication';
		}
	}

	async function fetch(info: RequestInfo | URL, init?: RequestInit): Promise<Response> {
		if (requestQueue.length > 0) {
			return new Promise((resolve, reject) => {
				requestQueue.push(() => {
					fetch(info, init).then(resolve).catch(reject);
				});
			});
		}

		const { account, ...headers } = (init?.headers ?? {}) as Record<string, string>;

		const identity =
			account === null ? undefined : account ? keychain.find(account) : keychain.all()[0];

		const response = await window.fetch(info, {
			...init,
			headers: identity
				? {
						...headers,
						authorization: 'Bearer ' + identity.accessToken,
				  }
				: headers,
		});

		if (identity) {
			try {
				const json = await response.clone().json();

				if (
					json['errors']?.[0]?.extensions?.error?.key === 'authorization.errors.invalidAccessToken'
				) {
					if (requestQueue.length === 0) {
						refresh(identity);
					}

					return new Promise((resolve, reject) => {
						requestQueue.push(() => {
							fetch(info, init).then(resolve).catch(reject);
						});
					});
				} else if (
					json['errors']?.[0]?.extensions?.error?.key?.startsWith('authorization.errors')
				) {
					keychain.delete();

					window.location.href = '/authentication';
				}
			} catch (_) {
				// What are we catching?
			}
		}

		return response;
	}

	return concat(
		new ApolloLink((operation, forward) => {
			operation.setContext(({ account, headers }: DefaultContext) => {
				return {
					headers: {
						...headers,
						account,
					},
				};
			});

			return forward(operation);
		}),
		new HttpLink({
			uri: import.meta.env.VITE_GRAPHQL ?? 'https://api.ontreams.com/graphql',
			fetch: (info, init) => fetch(info, init),
		}),
	);
}
